/**
 * 📘 INFORMATION AU PUBLIC — CONFORMITÉ QUALIOPI (Iter296 PREMIUM REWRITE)
 * ========================================================================
 *
 * Page d'information officielle sur la formation Coach DIA.
 * Accessible sans connexion, conforme aux exigences Qualiopi.
 * Refonte premium Iter296 — niveau page de vente SaaS / formation certifiante.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BookOpen, Target, Brain, Award, Users, CheckCircle, ChevronLeft,
  Layers, Headphones, Mic, FileText, Shield, Sparkles, Rocket, Eye,
  ArrowRight, MessageSquare, BarChart3, Lightbulb, Pencil,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

/* ─────────────────── Composants atomiques premium ─────────────────── */

const SectionTitle = ({ kicker, title, subtitle, align = 'left' }) => (
  <div className={`mb-8 ${align === 'center' ? 'text-center' : ''}`}>
    {kicker && (
      <p className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-3">{kicker}</p>
    )}
    <h2 className="text-3xl sm:text-4xl font-serif text-[#111827] tracking-tight leading-tight">{title}</h2>
    {subtitle && <p className="mt-3 text-base sm:text-lg text-[#374151] max-w-3xl">{subtitle}</p>}
  </div>
);

const Divider = () => (
  <div className="my-16 sm:my-20 flex items-center gap-4 max-w-5xl mx-auto px-4" aria-hidden>
    <span className="flex-1 h-px bg-gradient-to-r from-transparent via-[#E5DFD0] to-transparent" />
    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
    <span className="flex-1 h-px bg-gradient-to-r from-transparent via-[#E5DFD0] to-transparent" />
  </div>
);

const HighlightBlock = ({ icon: Icon, kicker, title, children, accent = 'gold', testid }) => {
  const palette = accent === 'dark'
    ? { bg: 'bg-gradient-to-br from-[#1F1A12] via-[#2A2319] to-[#1F1A12]', border: 'border-[#1F1A12]', text: 'text-white', muted: 'text-white/75', kickerColor: 'text-[#F3D060]', iconBg: 'bg-[#F3D060] text-[#1F1A12]', titleColor: 'text-white' }
    : { bg: 'bg-gradient-to-br from-white via-[#FBF8F1] to-[#F5EDDA]', border: 'border-[#D4AF37]/40', text: 'text-[#111827]', muted: 'text-[#374151]', kickerColor: 'text-[#92710A]', iconBg: 'bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] text-white', titleColor: 'text-[#111827]' };
  return (
    <motion.section
      {...fadeInUp}
      data-testid={testid}
      className={`relative overflow-hidden rounded-3xl border-2 ${palette.border} ${palette.bg} p-8 sm:p-12 shadow-[0_18px_44px_-18px_rgba(146,113,10,0.35)]`}
    >
      <span className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F3D060]/15 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative">
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${palette.iconBg} mb-5 shadow-md`}>
          <Icon className="w-6 h-6" />
        </div>
        {kicker && <p className={`text-[10px] tracking-[0.3em] uppercase ${palette.kickerColor} font-semibold mb-2`}>{kicker}</p>}
        <h3 className={`text-2xl sm:text-3xl font-serif ${palette.titleColor} mb-4 leading-tight`}>{title}</h3>
        <div className={`prose prose-base ${palette.text === 'text-white' ? 'prose-invert' : ''} max-w-none ${palette.muted}`}>
          {children}
        </div>
      </div>
    </motion.section>
  );
};

const FeatureCard = ({ icon: Icon, title, children }) => (
  <motion.div {...fadeInUp} className="bg-white border border-[#E5DFD0] rounded-2xl p-6 hover:border-[#D4AF37]/60 hover:shadow-[0_14px_32px_-14px_rgba(146,113,10,0.25)] hover:-translate-y-1 transition-all duration-300">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F3D060]/30 to-[#D4AF37]/20 flex items-center justify-center mb-4 text-[#92710A]">
      <Icon className="w-5 h-5" />
    </div>
    <h4 className="text-lg font-semibold text-[#111827] mb-2">{title}</h4>
    <p className="text-sm text-[#374151] leading-relaxed">{children}</p>
  </motion.div>
);

const PromiseChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5DFD0] text-[#374151] text-sm font-medium shadow-sm">
    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
    {children}
  </span>
);

const StepCard = ({ n, icon: Icon, title, children }) => (
  <motion.div {...fadeInUp} className="relative bg-white border border-[#E5DFD0] rounded-2xl p-6 pl-16">
    <span className="absolute left-5 top-6 w-9 h-9 rounded-full bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] text-white font-serif font-bold flex items-center justify-center shadow-md">{n}</span>
    <div className="flex items-center gap-2 text-[#92710A] mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Étape {n}</span>
    </div>
    <h4 className="text-lg font-semibold text-[#111827] mb-1.5">{title}</h4>
    <p className="text-sm text-[#374151] leading-relaxed">{children}</p>
  </motion.div>
);

const LevelRow = ({ pct, label, color }) => (
  <div className="flex items-center gap-4 py-3 border-b border-[#E5DFD0] last:border-0">
    <div className="shrink-0 w-16 sm:w-20 text-right tabular-nums font-serif text-2xl font-bold" style={{ color }}>{pct}%</div>
    <div className="flex-1">
      <div className="text-sm font-semibold text-[#111827]">{label}</div>
      <div className="mt-1.5 h-1.5 rounded-full bg-[#F3EFE5] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  </div>
);

/* ────────────────────────────── Page ────────────────────────────── */

const InformationPublicPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FBF8F1] to-[#F5EDDA]" data-testid="information-public-page">
      {/* Header sticky premium */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E5DFD0]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1F1A12] hover:text-[#92710A] transition-colors" data-testid="back-to-home">
            <ChevronLeft className="w-5 h-5" />
            <span className="font-semibold text-sm sm:text-base">Retour à l'accueil</span>
          </Link>
          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <Shield className="w-4 h-4 text-[#92710A]" />
            <span className="font-semibold text-[#92710A]">Conforme Qualiopi</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
        <motion.div {...fadeInUp}>
          <span className="inline-flex items-center gap-2 bg-[#1F1A12] text-[#F3D060] text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-8 shadow-md">
            <Rocket className="w-3.5 h-3.5" /> Coach DIA
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#111827] tracking-tight leading-[1.05] mb-6">
            La formation au management moderne,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#92710A] via-[#D4AF37] to-[#92710A]">augmentée par l'IA et le coaching humain</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#374151] max-w-3xl mx-auto leading-relaxed mb-8">
            Une plateforme de formation professionnelle conçue pour développer des compétences managériales <strong className="text-[#111827]">concrètes, observables et évaluées sur la pratique réelle</strong>. Ici, on ne sépare jamais la réflexion de l'action.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <PromiseChip>Vous comprenez</PromiseChip>
            <PromiseChip>Vous vous entraînez</PromiseChip>
            <PromiseChip>Vous progressez</PromiseChip>
            <PromiseChip>Vous êtes évalué sur des preuves</PromiseChip>
          </div>
        </motion.div>
      </section>

      <Divider />

      {/* Une formation structurée */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Architecture pédagogique"
          title="Une formation structurée, lisible et exigeante"
          subtitle="5 piliers fondamentaux du management moderne · 20 modules conçus comme des briques pédagogiques autonomes. Chaque module est construit pour produire un résultat visible dans votre pratique."
        />
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          <motion.div {...fadeInUp} className="rounded-3xl bg-white border-2 border-[#D4AF37]/30 p-8 shadow-[0_14px_36px_-16px_rgba(146,113,10,0.25)]">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] flex items-center justify-center mb-5 shadow-md">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <p className="text-5xl font-serif font-bold text-[#92710A] mb-2 tabular-nums">5</p>
            <h3 className="text-xl font-semibold text-[#111827] mb-2">Piliers fondamentaux</h3>
            <p className="text-sm text-[#374151] leading-relaxed">Les fondations indispensables du management moderne, structurées et progressives.</p>
          </motion.div>
          <motion.div {...fadeInUp} className="rounded-3xl bg-gradient-to-br from-[#1F1A12] via-[#2A2319] to-[#1F1A12] border-2 border-[#1F1A12] p-8 shadow-[0_14px_36px_-16px_rgba(31,26,18,0.45)]">
            <div className="w-12 h-12 rounded-2xl bg-[#F3D060] flex items-center justify-center mb-5 shadow-md">
              <BookOpen className="w-6 h-6 text-[#1F1A12]" />
            </div>
            <p className="text-5xl font-serif font-bold text-[#F3D060] mb-2 tabular-nums">20</p>
            <h3 className="text-xl font-semibold text-white mb-2">Modules autonomes</h3>
            <p className="text-sm text-white/75 leading-relaxed">Chaque module produit un résultat visible : objectifs, compétences visées, critères de validation.</p>
          </motion.div>
        </div>
      </section>

      <Divider />

      {/* Socle théorique */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Avant chaque entraînement"
          title="Un socle théorique solide"
          subtitle="Chaque module commence par un cours accessible — en lecture ou en audio. Comprenez les concepts clés, structurez votre pensée, préparez efficacement la mise en pratique."
        />
        <div className="grid sm:grid-cols-2 gap-5">
          <FeatureCard icon={BookOpen} title="Lecture structurée">
            Texte clair, hiérarchisé, accessible — pour ancrer les concepts à votre rythme.
          </FeatureCard>
          <FeatureCard icon={Headphones} title="Audio immersif">
            Voix d'Owen, votre coach IA pédagogique, pour apprendre en mobilité.
          </FeatureCard>
        </div>
      </section>

      <Divider />

      {/* Ce que vous allez développer */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Compétences réellement développées"
          title="Ce que vous allez transformer dans votre pratique"
          subtitle="À travers les 20 modules, vous développez 6 capacités structurantes — chacune avec ses objectifs, compétences visées et critères de validation explicites."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <FeatureCard icon={Target} title="Posture de manager moderne">Une présence assumée, lucide et ajustée à votre contexte.</FeatureCard>
          <FeatureCard icon={Users} title="Déléguer et orchestrer">Faire faire mieux, donner du sens, libérer de la valeur collective.</FeatureCard>
          <FeatureCard icon={MessageSquare} title="Communication claire">Une parole nette, qui informe, mobilise et arbitre.</FeatureCard>
          <FeatureCard icon={Lightbulb} title="Leadership relationnel">Un lien solide, exigeant, qui fait grandir les autres.</FeatureCard>
          <FeatureCard icon={BarChart3} title="Décision structurée">Une méthode rigoureuse face à l'incertitude et à la complexité.</FeatureCard>
          <FeatureCard icon={Brain} title="Intégration responsable de l'IA">Utiliser l'IA pour amplifier — sans diluer votre jugement.</FeatureCard>
        </div>
      </section>

      <Divider />

      {/* Comment vous progressez */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Méthode d'apprentissage"
          title="Comment vous progressez concrètement"
          subtitle="Un parcours en 5 étapes, piloté par Owen, votre coach IA pédagogique. Rien n'est laissé à l'intuition seule."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <StepCard n={1} icon={BookOpen} title="Cours théorique">Le socle conceptuel structuré, lu ou écouté, qui pose les fondations.</StepCard>
          <StepCard n={2} icon={Pencil} title="Entraînement écrit">Réflexion guidée pour ancrer la compréhension dans des cas concrets.</StepCard>
          <StepCard n={3} icon={Mic} title="Entraînement vocal">Mises en situation orales — la pratique qui révèle la maîtrise réelle.</StepCard>
          <StepCard n={4} icon={Sparkles} title="Feedback structuré">L'IA décode votre performance et vous oriente avec précision.</StepCard>
          <StepCard n={5} icon={Award} title="Validation sur preuves">Évaluation rigoureuse fondée sur ce que vous avez réellement produit.</StepCard>
        </div>
      </section>

      <Divider />

      {/* 🧠 Architecture unique IA + Coaching humain (BLOC CLÉ) */}
      <section className="max-w-5xl mx-auto px-4">
        <HighlightBlock icon={Brain} kicker="Architecture unique" title="IA et coaching humain, ensemble" testid="block-hybrid-architecture" accent="gold">
          <p className="lead text-lg sm:text-xl">
            <strong>Coach DIA ne repose pas uniquement sur l'IA.</strong> La plateforme intègre un dispositif hybride structuré, qui combine entraînement intensif par l'IA et accompagnement ciblé par des coachs humains.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 mt-7 not-prose">
            <div className="bg-white/70 backdrop-blur rounded-2xl p-6 border border-[#D4AF37]/30">
              <div className="flex items-center gap-2 text-[#92710A] mb-3"><Sparkles className="w-4 h-4" /><span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Le rôle de l'IA</span></div>
              <h4 className="font-serif text-xl text-[#111827] mb-3">Créer la répétition et la progression</h4>
              <ul className="space-y-2 text-sm text-[#374151]">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Vous entraîner en continu, sans contrainte d'agenda</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Structurer votre réflexion pas à pas</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Tester vos décisions sur des cas concrets</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Recevoir un feedback immédiat et précis</li>
              </ul>
            </div>
            <div className="bg-white/70 backdrop-blur rounded-2xl p-6 border border-[#D4AF37]/30">
              <div className="flex items-center gap-2 text-[#92710A] mb-3"><Users className="w-4 h-4" /><span className="text-[10px] tracking-[0.25em] uppercase font-semibold">Le rôle du coaching humain</span></div>
              <h4 className="font-serif text-xl text-[#111827] mb-3">Accélérer ce que l'IA a préparé</h4>
              <ul className="space-y-2 text-sm text-[#374151]">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Prise de recul sur des situations réelles</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Gestion de contextes complexes</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Confrontation constructive et bienveillante</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Ajustement fin de votre posture</li>
              </ul>
              <p className="mt-4 text-xs italic text-[#92710A]">Le coach humain ne remplace pas l'IA — il intervient là où la transformation devient plus exigeante.</p>
            </div>
          </div>
        </HighlightBlock>
      </section>

      <Divider />

      {/* 🎯 Continuité unique : analyse post-session (BLOC CLÉ) */}
      <section className="max-w-5xl mx-auto px-4">
        <HighlightBlock icon={Eye} kicker="Continuité unique" title="Chaque session de coaching est analysée" testid="block-post-session-analysis" accent="dark">
          <p className="lead text-lg sm:text-xl">
            Chaque session de coaching humain est <strong>enrichie automatiquement par une analyse IA avancée</strong>. Vous obtenez un rapport structuré, un feedback personnalisé pour vous, et un feedback dédié au coach (confidentiel).
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-7 not-prose">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/15">
              <FileText className="w-5 h-5 text-[#F3D060] mb-3" />
              <h4 className="font-semibold text-white mb-1.5">Rapport structuré</h4>
              <p className="text-xs text-white/70 leading-relaxed">Synthèse claire de la séance — points abordés, décisions, signaux faibles.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/15">
              <Target className="w-5 h-5 text-[#F3D060] mb-3" />
              <h4 className="font-semibold text-white mb-1.5">Feedback personnalisé</h4>
              <p className="text-xs text-white/70 leading-relaxed">Pour vous : leviers actionnables, axes de progression, prochain pas concret.</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/15">
              <Shield className="w-5 h-5 text-[#F3D060] mb-3" />
              <h4 className="font-semibold text-white mb-1.5">Feedback coach</h4>
              <p className="text-xs text-white/70 leading-relaxed">Dédié et confidentiel — pour ajuster la posture et la stratégie d'accompagnement.</p>
            </div>
          </div>
          <div className="mt-7 not-prose">
            <p className="text-xs tracking-[0.25em] uppercase text-[#F3D060] font-semibold mb-3">Pourquoi c'est différent du coaching classique</p>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-white/80">
              <p className="flex gap-2"><ArrowRight className="w-4 h-4 text-[#F3D060] shrink-0 mt-0.5" />Les échanges sont analysés</p>
              <p className="flex gap-2"><ArrowRight className="w-4 h-4 text-[#F3D060] shrink-0 mt-0.5" />Les apprentissages sont structurés</p>
              <p className="flex gap-2"><ArrowRight className="w-4 h-4 text-[#F3D060] shrink-0 mt-0.5" />La progression est mesurée</p>
              <p className="flex gap-2"><ArrowRight className="w-4 h-4 text-[#F3D060] shrink-0 mt-0.5" />La continuité est assurée</p>
            </div>
            <p className="mt-5 italic text-[#F3D060]">Rien n'est laissé à l'intuition seule. Chaque interaction produit de la valeur mesurable.</p>
          </div>
        </HighlightBlock>
      </section>

      <Divider />

      {/* Résultat */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle kicker="Résultat" title="Ce que cela change pour vous" align="center" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <FeatureCard icon={BarChart3} title="Progression accélérée">Un rythme d'apprentissage que peu de dispositifs égalent.</FeatureCard>
          <FeatureCard icon={Brain} title="Décisions plus solides">Une méthode qui structure et fiabilise vos choix.</FeatureCard>
          <FeatureCard icon={Award} title="Maîtrise de votre posture">Une présence ajustée, calibrée, observable.</FeatureCard>
          <FeatureCard icon={Sparkles} title="Impact durable">Une transformation qui s'inscrit dans la pratique réelle.</FeatureCard>
        </div>
      </section>

      <Divider />

      {/* Espace d'entraînement libre */}
      <section className="max-w-5xl mx-auto px-4">
        <motion.div {...fadeInUp} className="rounded-3xl bg-gradient-to-br from-white via-[#FBF8F1] to-[#F5EDDA] border-2 border-[#D4AF37]/30 p-8 sm:p-10 flex flex-col sm:flex-row items-start gap-6 shadow-[0_14px_36px_-16px_rgba(146,113,10,0.25)]">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] flex items-center justify-center shrink-0 shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-2">En complément du parcours</p>
            <h3 className="text-2xl sm:text-3xl font-serif text-[#111827] mb-3">Un espace d'entraînement libre</h3>
            <p className="text-[#374151] mb-3 leading-relaxed">Accessible à tout moment pour travailler sur :</p>
            <ul className="space-y-2 text-[#374151]">
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-1 shrink-0" /> Une situation réelle</li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-1 shrink-0" /> Une prise de décision</li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-1 shrink-0" /> Une difficulté managériale</li>
            </ul>
          </div>
        </motion.div>
      </section>

      <Divider />

      {/* Évaluation fondée sur la pratique */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Évaluation"
          title="Une évaluation fondée sur la pratique"
          subtitle="Vous êtes évalué uniquement sur des preuves : échanges écrits, mises en situation, décisions prises."
        />
        <Card className="bg-white border-[#E5DFD0] rounded-3xl shadow-[0_10px_30px_-15px_rgba(146,113,10,0.2)]">
          <CardContent className="p-6 sm:p-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-4">Niveaux de maîtrise</p>
            <LevelRow pct={40} label="Compétence en émergence" color="#D4AF37" />
            <LevelRow pct={60} label="Compétence en consolidation" color="#92710A" />
            <LevelRow pct={100} label="Compétence validée" color="#1F1A12" />
          </CardContent>
        </Card>
      </section>

      <Divider />

      {/* Suivi & traçabilité + Attestation */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle
          kicker="Tableau de bord & attestation"
          title="Suivi, traçabilité et reconnaissance officielle"
        />
        <div className="grid sm:grid-cols-2 gap-5">
          <FeatureCard icon={BarChart3} title="Tableau de bord complet">
            Progression par module, objectifs atteints, preuves collectées, validations obtenues. Données horodatées, traçables et opposables.
          </FeatureCard>
          <FeatureCard icon={Award} title="Attestation de formation">
            Délivrée à chaque module validé et mise à jour automatiquement. Inclut modules validés, compétences développées, période réelle de formation, évaluation par Owen.
          </FeatureCard>
        </div>
      </section>

      <Divider />

      {/* Public concerné */}
      <section className="max-w-5xl mx-auto px-4">
        <SectionTitle kicker="Public concerné" title="À qui s'adresse Coach DIA" subtitle="Aucun prérequis technique." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Users, title: 'Managers en poste' },
            { icon: Sparkles, title: 'Nouveaux managers' },
            { icon: Target, title: 'Chefs de projet' },
            { icon: Award, title: 'Responsables d\'équipe' },
          ].map((p) => (
            <motion.div key={p.title} {...fadeInUp} className="bg-white border border-[#E5DFD0] rounded-2xl p-5 text-center hover:border-[#D4AF37]/60 transition-all">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F3D060]/30 to-[#D4AF37]/20 flex items-center justify-center mx-auto mb-3 text-[#92710A]">
                <p.icon className="w-5 h-5" />
              </div>
              <p className="font-semibold text-[#111827] text-sm">{p.title}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Divider />

      {/* Conformité Qualiopi */}
      <section className="max-w-5xl mx-auto px-4">
        <motion.div {...fadeInUp} className="rounded-3xl bg-white border-2 border-[#92710A]/30 p-8 sm:p-10 shadow-[0_14px_36px_-16px_rgba(146,113,10,0.2)]">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#92710A] via-[#D4AF37] to-[#F3D060] flex items-center justify-center shadow-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold">Certifiable</p>
              <h3 className="text-2xl font-serif text-[#111827]">Conformité Qualiopi</h3>
            </div>
          </div>
          <p className="text-[#374151] mb-4">Coach DIA respecte les exigences Qualiopi :</p>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm text-[#374151]">
            <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Information claire avant entrée en formation</li>
            <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Objectifs pédagogiques explicites</li>
            <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Méthodes structurées</li>
            <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Évaluation sur preuves</li>
            <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" /> Suivi et traçabilité</li>
          </ul>
        </motion.div>
      </section>

      <Divider />

      {/* 🎯 En résumé (BLOC CLÉ FINAL) */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <HighlightBlock icon={Rocket} kicker="En résumé" title="Coach DIA, une formation qui mesure, analyse et transforme" testid="block-summary" accent="gold">
          <p className="lead text-lg sm:text-xl">
            Une formation complète et exigeante :
          </p>
          <ul className="space-y-3 text-base mt-5 not-prose">
            <li className="flex gap-3 items-start"><CheckCircle className="w-5 h-5 text-[#92710A] mt-0.5 shrink-0" /><span><strong>Vous comprenez</strong> grâce aux cours.</span></li>
            <li className="flex gap-3 items-start"><CheckCircle className="w-5 h-5 text-[#92710A] mt-0.5 shrink-0" /><span><strong>Vous progressez</strong> grâce à l'entraînement.</span></li>
            <li className="flex gap-3 items-start"><CheckCircle className="w-5 h-5 text-[#92710A] mt-0.5 shrink-0" /><span><strong>Vous êtes accompagné</strong> par l'IA et des coachs humains.</span></li>
            <li className="flex gap-3 items-start"><CheckCircle className="w-5 h-5 text-[#92710A] mt-0.5 shrink-0" /><span><strong>Vous êtes évalué</strong> sur votre pratique réelle.</span></li>
          </ul>
          <p className="mt-7 text-base sm:text-lg italic text-[#92710A]">
            Une plateforme qui ne se contente pas de former. Elle mesure, analyse et transforme durablement vos compétences.
          </p>
        </HighlightBlock>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5DFD0] bg-white py-10">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#374151]">
          <p className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#92710A]" />
            <span>Conforme Qualiopi — Formation certifiable</span>
          </p>
          <p className="text-xs text-[#6B5E48]">© 2026 Coach DIA — Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
};

export default InformationPublicPage;
